/**
 * S3 1:1 대화 (DEV_SPEC 4.4, 시안 03 README S3·N3·C1).
 *
 *   상단 바(뒤로, [아바타 32 + 호칭 / 소개 한 줄] → 프로필, ☰) → (별채대화에서 왔으면) 「별채대화에서 이어 왔어요」
 *   → 대화 → 입력 중 → 입력창(세그먼트 없음)
 *
 * - 뒤로 = 별채대화(어느 길로 들어와도). 고민 스레드에서 들어왔을 때만 고민 스레드로 (N3)
 * - 답이 나오는 동안 입력칸은 「○○가 답하는 중」으로 잠기고, 전송 자리에 멈추기 버튼 (C1)
 *
 * - 답은 스트리밍으로 흘러나오고, 그동안 전송 버튼 자리에 멈추기 버튼이 섭니다
 * - 토스 카드를 누르면 받는 멤버의 1:1 로 갑니다. 뒤로 가면 이 대화로 돌아옵니다
 * - 대화에는 근거 칩을 붙이지 않습니다(D18). 저서·사료를 참고했으면 멤버가 말 안에서 밝힙니다(「손자병법에 따르면」)
 * - 위기 신호가 있으면 멤버가 말투를 지키며 안부를 묻고, 안전 카드가 대화에 남습니다
 * - 바닥 근처에 있을 때만 새 답을 따라 내려갑니다 (히어로챗 D70)
 * - 이모 1:1 은 재방문 카운터입니다. 첫 방문 대화의 장면 줄(기척·곁말·올린 말)이 그대로 보이고,
 *   이모가 사주를 다시 물으면 「봐주세요」 카드가, 별채를 다시 권하면 「별채에 한 줄 보내기」 카드가 붙습니다
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { MessagesSquare } from 'lucide-react';
import { getMember } from '@config/members/index.js';
import { withJosa } from '@shared/korean.js';
import { ChatTopBar } from '../../components/TopBar.jsx';
import { MemoEditSheet, MemoLine } from '../../components/memo/Memo.jsx';
import { useNoteSheet } from '../../stores/noteSheet.js';
import { MemberMemoSheet } from '../../components/memo/MemberMemoSheet.jsx';
import { NotebookPen } from 'lucide-react';
import { useNav } from '../../stores/nav.js';
import { MemberMessage, MyMessage } from '../../components/MessageBubbles.jsx';
import { TypingIndicator } from '../../components/TypingIndicator.jsx';
import { SafetyCard } from '../../components/SafetyCard.jsx';
import { Composer } from '../../components/Composer.jsx';
import { ChipRow } from '../../components/ChipRow.jsx';
import { track } from '../../api/track.js';
import { Button } from '../../components/Button.jsx';
import { SceneLine } from '../../components/SceneLine.jsx';
import { BrandMark } from '../../components/Brand.jsx';
import { BirthSheet } from '../../components/birth/BirthSheet.jsx';
import { errorMessageOf, isAbortError } from '../../api/client.js';
import * as chatApi from '../../api/chat.js';
import { TossCard } from './TossCard.jsx';
import { useChatTurn } from './useChatTurn.js';
import { noteRevealSpeed } from '../../stores/revealSpeed.js';

const FOLLOW_THRESHOLD_PX = 48;

function appendMessage(messages, message) {
    if (messages.some((existing) => existing.messagesId === message.messagesId)) {
        return messages;
    }
    return [...messages, message];
}

export function ChatPage() {
    const { memberKey } = useParams();
    const member = getMember(memberKey);
    const navigate = useNavigate();
    // 내 말 길게 누르기 「이모 수첩에 적기」(시안 13 N6, C-31). 기본은 이 멤버만 알게
    const openNoteSheet = useNoteSheet((state) => state.open);
    const location = useLocation();
    const cameFrom = location.state?.from ?? null;
    const refreshChats = useNav((state) => state.refreshChats);
    const fromRoom = Boolean(location.state?.fromRoom);
    // 고민 글 · 상회칠판 글 답글에서 이어 옴 (시안 05 S2c): 맨 위 인용 상자
    const fromReply = location.state?.fromReply ?? null;
    // 「오늘은 이모랑만요」로 단골이 된 바로 그때 (시안 03 O1)
    const justJoined = Boolean(location.state?.justJoined);
    // 뒤로 = 별채대화. 고민 스레드에서 왔으면 그리로 (N3)
    const goBack = useCallback(() => {
        if (cameFrom === 'worry' && (window.history.state?.idx ?? 0) > 0) {
            navigate(-1);
            return;
        }
        navigate('/', { replace: true });
    }, [cameFrom, navigate]);
    const scrollRef = useRef(null);
    const shouldFollowRef = useRef(true);

    const [chat, setChat] = useState({ status: 'loading', messages: [], hasMore: false, error: null });
    const [draft, setDraft] = useState('');
    const [followingTossId, setFollowingTossId] = useState(null);
    const [tossError, setTossError] = useState(null);
    const [collapsedSafety, setCollapsedSafety] = useState(() => new Set());
    const [memoSheetOpen, setMemoSheetOpen] = useState(false);
    const [editingMemo, setEditingMemo] = useState(null);
    const [birthSheetOpen, setBirthSheetOpen] = useState(false);
    // 대화 칩(C-29 · D74): 입력창 위, 입력칸이 비어 있을 때만
    const [chips, setChips] = useState(null);
    // 이번 화면에서 답한 장면 카드(사주 재요청). 답하면 버튼을 거둡니다
    const [answeredScenes, setAnsweredScenes] = useState(() => new Set());

    const load = useCallback(
        async (signal) => {
            try {
                const data = await chatApi.fetchChat(memberKey, { signal });
                noteRevealSpeed(data.revealSpeed);
                setChat({ status: 'ready', messages: data.messages, hasMore: data.hasMore, error: null });
                setChips(data.chips ?? null);
                // 이 대화를 열어 읽은 것으로 바뀌었습니다. ☰ 배지를 다시 셉니다
                refreshChats();
            } catch (error) {
                if (!isAbortError(error)) {
                    setChat((previous) => ({
                        ...previous,
                        status: 'error',
                        error: errorMessageOf(error, '대화를 불러오지 못했어요.'),
                    }));
                }
            }
        },
        [memberKey, refreshChats]
    );

    useEffect(() => {
        if (!member) {
            return undefined;
        }
        const controller = new AbortController();
        setChat({ status: 'loading', messages: [], hasMore: false, error: null });
        shouldFollowRef.current = true;
        load(controller.signal);
        return () => {
            controller.abort();
        };
    }, [load, member]);

    const turn = useChatTurn(memberKey, {
        onUserMessage(message) {
            shouldFollowRef.current = true;
            setDraft('');
            setChat((previous) => ({ ...previous, messages: appendMessage(previous.messages, message) }));
        },
        onMemberMessage(message) {
            setChat((previous) => ({ ...previous, messages: appendMessage(previous.messages, message) }));
        },
        onRestoreDraft(text, acceptedMessagesId) {
            // 한 글자도 안 나온 채 멈췄습니다. 서버가 내 말을 거뒀으므로 화면에서도 걷고 입력창으로 돌립니다.
            setChat((previous) => ({
                ...previous,
                messages: previous.messages.filter((message) => message.messagesId !== acceptedMessagesId),
            }));
            setDraft(text);
        },
        onChips(next) {
            setChips(next);
        },
        onResync() {
            load();
        },
    });

    useLayoutEffect(() => {
        const element = scrollRef.current;
        if (element && shouldFollowRef.current) {
            element.scrollTop = element.scrollHeight;
        }
    }, [chat.messages, turn.streamText, turn.phase]);

    function handleScroll() {
        const element = scrollRef.current;
        if (element) {
            shouldFollowRef.current =
                element.scrollHeight - element.scrollTop - element.clientHeight < FOLLOW_THRESHOLD_PX;
        }
    }

    async function handleFollowToss(message) {
        setFollowingTossId(message.messagesId);
        setTossError(null);
        try {
            const { toMemberKey } = await chatApi.followToss(memberKey, message.messagesId);
            navigate(`/chat/${toMemberKey}`);
        } catch (error) {
            setTossError(errorMessageOf(error, '넘어가지 못했어요. 잠시 후 다시 시도해 주세요.'));
        } finally {
            setFollowingTossId(null);
        }
    }

    function toggleSafety(messagesId, collapsed) {
        setCollapsedSafety((previous) => {
            const next = new Set(previous);
            if (collapsed) {
                next.add(messagesId);
            } else {
                next.delete(messagesId);
            }
            return next;
        });
    }

    if (!member) {
        return <Navigate to="/members" replace />;
    }

    return (
        <div className="flex h-dvh flex-col">
            <ChatTopBar
                memberKey={memberKey}
                onBack={goBack}
                onProfile={() => navigate(`/members/${memberKey}`, { state: { from: 'chat' } })}
                onMemo={() => setMemoSheetOpen(true)}
            />

            <main
                ref={scrollRef}
                onScroll={handleScroll}
                aria-live="polite"
                className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4"
            >
                {justJoined ? (
                    <span className="flex h-7 items-center gap-1.5 self-center rounded-[14px] bg-surface-sunken px-3 text-micro">
                        <BrandMark size={14} />
                        인생상회 단골이 됐어요
                    </span>
                ) : null}
                {fromRoom ? (
                    <span className="flex h-7 items-center gap-1.5 self-center rounded-[14px] bg-surface-sunken px-3 text-micro">
                        <MessagesSquare size={14} strokeWidth={1.6} aria-hidden="true" />
                        별채대화에서 이어 왔어요
                    </span>
                ) : null}
                {fromReply ? (
                    <div className="flex flex-col gap-1.5 rounded-[12px] border border-hairline bg-surface px-3.5 py-3">
                        <span className="flex items-center gap-1.5 text-[12px] font-semibold">
                            <MessagesSquare size={14} strokeWidth={1.6} aria-hidden="true" />
                            {fromReply.label ?? '고민 글에서 이어 왔어요'}
                        </span>
                        <span className="truncate text-[13px] text-text-secondary">{fromReply.postLine}</span>
                        <span className="font-serif text-[14px] leading-[22px]">{fromReply.replyBody}</span>
                    </div>
                ) : null}
                {chat.status === 'loading' ? (
                    <p className="m-0 py-10 text-center text-caption text-text-secondary">대화를 펼치는 중이에요.</p>
                ) : null}
                {chat.status === 'error' ? (
                    <div className="flex flex-col items-center gap-3 py-10 text-center">
                        <p role="alert" className="m-0 text-caption text-error">
                            {chat.error}
                        </p>
                        <Button variant="secondary" size="md" onClick={() => load()}>
                            다시 불러오기
                        </Button>
                    </div>
                ) : null}

                {chat.messages.map((message) => {
                    if (message.role === 'system' && message.scene?.type === 'birthOffer') {
                        return (
                            <BirthOfferCard
                                key={message.messagesId}
                                answered={answeredScenes.has(message.messagesId)}
                                onAccept={() => {
                                    setAnsweredScenes((previous) => new Set(previous).add(message.messagesId));
                                    setBirthSheetOpen(true);
                                }}
                                onDecline={() => {
                                    setAnsweredScenes((previous) => new Set(previous).add(message.messagesId));
                                }}
                            />
                        );
                    }
                    if (message.role === 'system' && message.scene?.type === 'teaOffer') {
                        return (
                            <TeaChips
                                key={message.messagesId}
                                message={message}
                                answered={
                                    answeredScenes.has(message.messagesId) ||
                                    chat.messages.some(
                                        (later) => later.role === 'user' && later.messagesId > message.messagesId
                                    )
                                }
                                onChoose={async (tea) => {
                                    setAnsweredScenes((previous) => new Set(previous).add(message.messagesId));
                                    try {
                                        const result = await chatApi.chooseRevisitTea(message.messagesId, tea);
                                        setChat((previous) => ({
                                            ...previous,
                                            messages: result.messages.reduce(appendMessage, previous.messages),
                                        }));
                                    } catch (error) {
                                        setTossError(errorMessageOf(error, '차를 고르지 못했어요.'));
                                    }
                                }}
                            />
                        );
                    }
                    if (message.role === 'system' && message.scene?.type === 'boardInvite') {
                        return (
                            <Button
                                key={message.messagesId}
                                variant="secondary"
                                size="md"
                                className="ml-10 self-start"
                                onClick={() => navigate('/')}
                            >
                                별채에 한 줄 보내기
                            </Button>
                        );
                    }
                    // 이모 수첩 줄 (04 D2a) · 이어 묻기 알약 「지난번 얘기 이어서」 (04 D2d)
                    if (message.role === 'system' && message.scene?.type === 'memo') {
                        return (
                            <MemoLine
                                key={message.messagesId}
                                byUser={Boolean(message.scene.byUser)}
                                onEdit={() =>
                                    setEditingMemo({
                                        memoriesId: message.scene.memoriesId,
                                        text: message.scene.text,
                                        scope: message.scene.scope,
                                        memberKey,
                                        createdAt: message.createdAt,
                                    })
                                }
                            />
                        );
                    }
                    if (message.role === 'system' && message.scene?.type === 'loopFollowup') {
                        return (
                            <div key={message.messagesId} className="flex justify-center">
                                <span className="flex h-7 items-center gap-1.5 rounded-[14px] bg-surface-sunken px-3 text-[12px] font-semibold">
                                    <NotebookPen size={14} strokeWidth={1.6} aria-hidden="true" />
                                    지난번 얘기 이어서
                                </span>
                            </div>
                        );
                    }
                    if (message.role === 'system' && message.scene && message.scene.type !== 'safety') {
                        return (
                            <SceneLine
                                key={message.messagesId}
                                scene={message.scene}
                                onOpenProfile={() => navigate('/members/imo', { state: { from: 'chat' } })}
                            />
                        );
                    }
                    if (message.role === 'system') {
                        return (
                            <SafetyCard
                                key={message.messagesId}
                                level={message.safetyLevel}
                                collapsed={collapsedSafety.has(message.messagesId)}
                                onCollapse={() => toggleSafety(message.messagesId, true)}
                                onExpand={() => toggleSafety(message.messagesId, false)}
                            />
                        );
                    }
                    if (message.role === 'user') {
                        return (
                            <MyMessage
                                key={message.messagesId}
                                body={message.body}
                                onNote={() =>
                                    openNoteSheet({
                                        entry: 'long_press',
                                        draft: message.body,
                                        scope: `member:${memberKey}`,
                                        origin: { messageId: message.messagesId, surface: 'chat', memberKey },
                                        key: `press-${message.messagesId}`,
                                        // 그 말 아래 「내가 수첩에 적었어요」 줄을 보이려고 다시 읽습니다
                                        onSaved: () => load(),
                                    })
                                }
                            />
                        );
                    }
                    return (
                        <MemberMessage
                            key={message.messagesId}
                            memberKey={memberKey}
                            name={member.name}
                            showName={false}
                            body={message.body}
                        >
                            {message.stopped ? (
                                <span className="text-micro font-normal text-text-secondary">여기서 멈췄어요.</span>
                            ) : null}
                            {message.tossMemberKey ? (
                                <TossCard
                                    memberKey={message.tossMemberKey}
                                    busy={followingTossId === message.messagesId}
                                    onFollow={() => handleFollowToss(message)}
                                />
                            ) : null}
                        </MemberMessage>
                    );
                })}

                {turn.phase === 'streaming' ? (
                    <MemberMessage
                        memberKey={memberKey}
                        name={member.name}
                        showName={false}
                        streaming
                        body={turn.streamText}
                    />
                ) : null}
                {/* 내 말이 먼저 붙고, 서버가 받은 뒤에 입력 중 (사용자 지정 2026-10-13) */}
                {turn.phase === 'sending' && turn.pendingText ? (
                    <MyMessage body={turn.pendingText} status="sending" />
                ) : null}
                {turn.phase === 'thinking' ? <TypingIndicator memberKey={memberKey} name={member.name} /> : null}
            </main>

            {turn.error || tossError ? (
                <p role="alert" className="m-0 border-t border-hairline bg-bg px-4 pt-2 text-caption text-error">
                    {turn.error ?? tossError}
                </p>
            ) : null}
            {chips?.items?.length && !draft.trim() && !turn.generating ? (
                <ChipRow
                    chips={chips}
                    surface="chat"
                    onTap={(item) => {
                        // 다른 멤버에게 묻는 칩은 1:1 에서는 토스(4.4): 그 멤버 1:1 로
                        if (item.type === 'ask_other' && item.target) {
                            track('message_send', { source: 'chip_toss', mentions: 0 });
                            setChips(null);
                            navigate(`/chat/${item.target}`);
                            return;
                        }
                        track('message_send', { source: 'chip', mentions: 0 });
                        shouldFollowRef.current = true;
                        turn.send(item.text, { chipItemsId: item.chipItemsId });
                    }}
                />
            ) : null}
            <Composer
                value={draft}
                onChange={setDraft}
                onSubmit={turn.send}
                placeholder={`${member.name}에게 말 걸기`}
                generatingPlaceholder={`${withJosa(member.name, '이', '가')} 답하는 중`}
                generating={turn.generating}
                onStop={turn.stop}
                maxLength={2000}
            />

            <BirthSheet
                open={birthSheetOpen}
                title="이모한테 생일 알려주기"
                onClose={() => setBirthSheetOpen(false)}
                onSaved={() => setBirthSheetOpen(false)}
            />
            <MemberMemoSheet memberKey={memberKey} open={memoSheetOpen} onClose={() => setMemoSheetOpen(false)} />
            <MemoEditSheet memory={editingMemo} onClose={() => setEditingMemo(null)} />
        </div>
    );
}

/** 재방문 카운터의 차 칩 (시안 03 R1): 「네, 유자차요」 / 「오늘은 보리차요」 */
function TeaChips({ message, answered, onChoose }) {
    if (answered) {
        return null;
    }
    return (
        <div className="ml-10 flex flex-wrap gap-2">
            {(message.scene.choices ?? []).map((choice) => (
                <button
                    key={choice.tea}
                    type="button"
                    onClick={() => onChoose(choice.tea)}
                    className="h-10 rounded-[20px] border border-chip-line bg-surface px-4 text-[15px]"
                >
                    {choice.label}
                </button>
            ))}
        </div>
    );
}

/** 이모의 사주 재요청 (DEV_SPEC 4.1). 「봐주세요」면 사주 입력 시트를 띄웁니다 */
function BirthOfferCard({ answered, onAccept, onDecline }) {
    if (answered) {
        return null;
    }
    return (
        <div className="ml-10 flex gap-2">
            <Button size="sm" onClick={onAccept}>
                봐주세요
            </Button>
            <Button variant="secondary" size="sm" onClick={onDecline}>
                다음에요
            </Button>
        </div>
    );
}
